import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { groupsAPI } from '../api';
import Footer from './Footer';

function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    organizer_opts_out: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/groups');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await groupsAPI.createGroup(
        formData.name,
        formData.description,
        formData.organizer_opts_out
      );
      navigate(`/groups/${response.data.group.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create group');
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="snowflakes" aria-hidden="true">
          <div className="snowflake">❅</div>
          <div className="snowflake">❆</div>
          <div className="snowflake">❅</div>
          <div className="snowflake">❆</div>
          <div className="snowflake">❅</div>
          <div className="snowflake">❆</div>
          <div className="snowflake">❅</div>
          <div className="snowflake">❆</div>
        </div>

        <div className="hero-content">
          <div className="hero-icon">🎅</div>
          <h1 className="hero-title">Who Is Your Santa?</h1>
          {/* <p className="hero-subtitle">The Ultimate Secret Santa Platform</p> */}
          <p className="hero-description">
            Discover the mystery! Create groups, invite friends, and reveal your Secret Santa matches with ease.
          </p>

          <div className="hero-buttons">
            <button
              className="btn btn-hero-primary"
              onClick={() => navigate('/login')}
            >
              Login
            </button>
            <button
              className="btn btn-hero-secondary"
              onClick={() => {
                const featuresSection = document.querySelector('.features-section');
                featuresSection?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              How It Works
            </button>
          </div>
          <p style={{ marginTop: '20px', fontSize: '0.9rem', opacity: '0.9', color: 'white' }}>
            🎁 Ask a friend to invite you to their group or{' '}
            <a
              href="/waitlist"
              onClick={(e) => {
                e.preventDefault();
                navigate('/waitlist');
              }}
              style={{
                color: 'white',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              join the waitlist
            </a>
            !
          </p>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <h2 className="features-title">How It Works</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🎄</div>
            <h3>Create Your Group</h3>
            <p>Set up a Secret Santa group in seconds with a custom name and description.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">✉️</div>
            <h3>Invite Friends</h3>
            <p>Send email invitations or share a unique join link with your participants.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🎁</div>
            <h3>Set Restrictions</h3>
            <p>Prevent specific people from being matched together (perfect for couples!).</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🎲</div>
            <h3>Auto-Assign</h3>
            <p>Let our smart algorithm create perfect Secret Santa matches instantly.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🎭</div>
            <h3>Secret Riddle Reveal</h3>
            <p>After assignments we conjure a playful AI-crafted riddle for everyone to enjoy while keeping the mystery alive.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🔒</div>
            <h3>Stay Secret</h3>
            <p>Everyone only sees who they're giving to—the secret stays safe!</p>
          </div>
        </div>
      </div>

      {/* CTA + Footer */}
      <div className="cta-section">
        <div className="cta-content">
          <h2>Ready to Spread Holiday Cheer?</h2>
          <p>Start your Secret Santa group today—it's free and easy!</p>
          <button
            className="btn btn-cta"
            onClick={() => navigate('/login')}
          >
            Create Your Group Now
          </button>
        </div>
      </div>
      <Footer />

      {/* Create Group Dialog */}
      {showCreateDialog && (
        <div className="dialog-overlay" onClick={() => setShowCreateDialog(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>Create Secret Santa Group</h2>
              <button
                className="dialog-close"
                onClick={() => setShowCreateDialog(false)}
              >
                ×
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Group Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Family Christmas 2025"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description (Optional)</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Add details about your Secret Santa..."
                  rows="3"
                />
              </div>

              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="organizer_opts_out"
                  name="organizer_opts_out"
                  checked={formData.organizer_opts_out}
                  onChange={handleChange}
                />
                <label htmlFor="organizer_opts_out">
                  I want to organize but not participate
                </label>
              </div>

              <div className="dialog-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateDialog(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Landing;
